import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatError, logDev } from "../api";
import {
  Plus, Save, Trash2, Upload, Eye, EyeOff, X, Loader2, ImagePlus, ImageOff, RefreshCw,
} from "lucide-react";

const EMPTY_LAB = {
  code: "", name: "", tagline: "", description: "",
  color: "#0047AB", category: "", cover_image: "",
  skills: [], keywords: [], allowed_roles_governance: [],
  hidden: false,
};

function LabRow({ lab, onEdit, onToggleHidden, onDelete, onUploadCover, onDeleteCover, busy }) {
  const coverFile = useRef(null);
  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-xl border transition ${lab.hidden ? "opacity-60 bg-paper" : "bg-white"}`}
      style={{ borderColor: "var(--rule)" }}
      data-testid={`lab-row-${lab.id}`}
    >
      <div
        className="w-16 h-16 rounded-md overflow-hidden grid place-items-center shrink-0 relative cursor-pointer group"
        style={{ background: `${lab.color}15`, border: `2px solid ${lab.color}40` }}
        onClick={() => coverFile.current?.click()}
        title="Click to replace cover image"
      >
        {lab.cover_image ? (
          <img src={lab.cover_image} alt={lab.name} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <ImagePlus size={20} style={{ color: lab.color }} />
        )}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
          <Upload size={16} className="text-white" />
        </div>
        <input
          ref={coverFile}
          type="file"
          className="hidden"
          accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
          onChange={(e) => onUploadCover(lab, e.target.files?.[0])}
          data-testid={`lab-cover-input-${lab.id}`}
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[10px] font-bold uppercase px-1.5 py-0.5 rounded text-white" style={{ background: lab.color }}>
            {lab.code}
          </span>
          <span className="font-semibold text-sm truncate" data-testid={`lab-name-${lab.id}`}>{lab.name}</span>
          {lab.hidden && (
            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
              Hidden
            </span>
          )}
        </div>
        <div className="text-xs text-muted mt-0.5 truncate">{lab.tagline || lab.description?.slice(0, 100) || "—"}</div>
        <div className="text-[10px] text-muted mt-0.5">
          {lab.category} · {lab.module_count ?? 0} modules · {lab.enrolled_count ?? 0} enrolled
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {lab.cover_image && (
          <button
            type="button"
            onClick={() => onDeleteCover(lab)}
            disabled={busy}
            title="Remove cover image"
            aria-label="Remove cover image"
            data-testid={`lab-cover-delete-${lab.id}`}
            className="w-9 h-9 grid place-items-center text-muted hover:text-red-600 hover:bg-red-50 rounded-md transition disabled:opacity-50"
          >
            <ImageOff size={15} />
          </button>
        )}
        <button
          type="button"
          onClick={() => onEdit(lab)}
          disabled={busy}
          title="Edit"
          aria-label="Edit"
          data-testid={`lab-edit-${lab.id}`}
          className="px-3 py-1.5 text-xs font-semibold text-signal hover:bg-paper rounded-md transition disabled:opacity-50"
        >
          Edit
        </button>
        <button
          type="button"
          onClick={() => onToggleHidden(lab)}
          disabled={busy}
          title={lab.hidden ? "Show on public site" : "Hide from public site"}
          aria-label={lab.hidden ? "Unhide" : "Hide"}
          data-testid={`lab-toggle-hidden-${lab.id}`}
          className="w-9 h-9 grid place-items-center text-muted hover:text-ink hover:bg-paper rounded-md transition disabled:opacity-50"
        >
          {lab.hidden ? <Eye size={15} /> : <EyeOff size={15} />}
        </button>
        <button
          type="button"
          onClick={() => onDelete(lab)}
          disabled={busy}
          title="Delete permanently"
          aria-label="Delete"
          data-testid={`lab-delete-${lab.id}`}
          className="w-9 h-9 grid place-items-center text-muted hover:text-red-600 hover:bg-red-50 rounded-md transition disabled:opacity-50"
        >
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  );
}

function LabEditModal({ lab, onClose, onSaved }) {
  const isNew = !lab.id;
  const [form, setForm] = useState({ ...EMPTY_LAB, ...lab });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setForm({ ...form, [k]: v });
  const setList = (k, v) => set(k, v.split(",").map(s => s.trim()).filter(Boolean));

  const save = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    setBusy(true); setErr("");
    try {
      const body = {
        code: form.code.trim(),
        name: form.name.trim(),
        tagline: form.tagline?.trim() || null,
        description: form.description?.trim() || null,
        color: form.color || "#0047AB",
        category: form.category?.trim() || null,
        cover_image: form.cover_image?.trim() || null,
        hidden: !!form.hidden,
        skills: Array.isArray(form.skills) ? form.skills : [],
        keywords: Array.isArray(form.keywords) ? form.keywords : [],
        allowed_roles_governance: Array.isArray(form.allowed_roles_governance) ? form.allowed_roles_governance : [],
      };
      if (isNew) {
        await api.post("/admin/lms/labs", body);
      } else {
        await api.put(`/admin/lms/labs/${lab.id}`, body);
      }
      onSaved?.();
      onClose?.();
    } catch (e2) {
      setErr(formatError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="lab-edit-modal">
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <form onSubmit={save} className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <header className="p-5 border-b flex items-start justify-between gap-3 sticky top-0 bg-white z-10" style={{ borderColor: "var(--rule)" }}>
          <div>
            <div className="eyebrow">CoE Labs</div>
            <h2 className="font-display font-extrabold text-xl mt-1">{isNew ? "Add new lab" : `Edit ${lab.name}`}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="w-9 h-9 rounded-md hover:bg-paper grid place-items-center" data-testid="lab-edit-close">
            <X size={18} />
          </button>
        </header>

        <div className="p-5 space-y-4">
          {err && <div className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2" data-testid="lab-edit-error">{err}</div>}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="eyebrow">Code (governance taxonomy)</label>
              <input
                className="input mt-1.5 font-mono"
                value={form.code}
                onChange={(e) => set("code", e.target.value.toUpperCase().slice(0, 12))}
                placeholder="e.g. 3CI"
                required
                disabled={!isNew}
                data-testid="lab-edit-code"
              />
              {!isNew && <p className="text-[10px] text-muted mt-1">Code is immutable — used as the SC governance domain.</p>}
            </div>
            <div>
              <label className="eyebrow">Brand color</label>
              <input
                type="color"
                className="input mt-1.5 h-[42px] cursor-pointer"
                value={form.color}
                onChange={(e) => set("color", e.target.value)}
                data-testid="lab-edit-color"
              />
            </div>
          </div>

          <div>
            <label className="eyebrow">Name</label>
            <input
              className="input mt-1.5"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Cybersecurity, Cloud & Critical Infrastructure Lab"
              required
              maxLength={160}
              data-testid="lab-edit-name"
            />
          </div>

          <div>
            <label className="eyebrow">Tagline (short)</label>
            <input
              className="input mt-1.5"
              value={form.tagline}
              onChange={(e) => set("tagline", e.target.value)}
              placeholder="OT/IT security convergence, SOC training & critical infrastructure defence."
              maxLength={240}
              data-testid="lab-edit-tagline"
            />
          </div>

          <div>
            <label className="eyebrow">Description (long form)</label>
            <textarea
              className="input mt-1.5 min-h-[120px]"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              maxLength={4000}
              data-testid="lab-edit-description"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="eyebrow">Category badge</label>
              <input
                className="input mt-1.5"
                value={form.category}
                onChange={(e) => set("category", e.target.value)}
                placeholder="CYBERSECURITY"
                maxLength={40}
                data-testid="lab-edit-category"
              />
            </div>
            <div>
              <label className="eyebrow">Cover image URL (or upload from list)</label>
              <input
                className="input mt-1.5"
                value={form.cover_image}
                onChange={(e) => set("cover_image", e.target.value)}
                placeholder="/api/objstore/skillconnect/lab-covers/... or https://…"
                data-testid="lab-edit-cover-url"
              />
            </div>
          </div>

          <div>
            <label className="eyebrow">Skills (comma-separated)</label>
            <input
              className="input mt-1.5"
              defaultValue={(form.skills || []).join(", ")}
              onChange={(e) => setList("skills", e.target.value)}
              placeholder="cybersecurity, soc, incident response"
              data-testid="lab-edit-skills"
            />
          </div>

          <div>
            <label className="eyebrow">Keywords (governance classifier)</label>
            <input
              className="input mt-1.5"
              defaultValue={(form.keywords || []).join(", ")}
              onChange={(e) => setList("keywords", e.target.value)}
              placeholder="cyber, OT security, critical infra"
              data-testid="lab-edit-keywords"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.hidden}
              onChange={(e) => set("hidden", e.target.checked)}
              data-testid="lab-edit-hidden"
            />
            <span className="text-sm">Hide from public CoE Labs page</span>
          </label>
        </div>

        <footer className="p-5 border-t flex items-center justify-end gap-2 sticky bottom-0 bg-white" style={{ borderColor: "var(--rule)" }}>
          <button type="button" onClick={onClose} className="btn-secondary" data-testid="lab-edit-cancel">Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60" data-testid="lab-edit-submit">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {busy ? "Saving…" : "Save lab"}
          </button>
        </footer>
      </form>
    </div>
  );
}

export default function AdminCoeLabs() {
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      // Admin sees ALL labs, including hidden — bypass the public filter.
      const r = await api.get("/admin/lms/labs");
      setLabs(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      // Fallback: if /admin/lms/labs doesn't exist, hit public + use it (no hidden labs).
      try {
        const r2 = await api.get("/lms/labs");
        setLabs(Array.isArray(r2.data) ? r2.data : []);
        logDev.warn("[admin-labs] using public endpoint — hidden labs will not appear");
      } catch (e2) {
        setErr(formatError(e2 || e));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleHidden = async (lab) => {
    setBusy(true); setErr(""); setMsg("");
    try {
      await api.patch(`/admin/lms/labs/${lab.id}/hidden`, { hidden: !lab.hidden });
      setMsg(lab.hidden ? `${lab.code} is now visible on the public site.` : `${lab.code} hidden from the public site.`);
      await load();
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const uploadCover = async (lab, file) => {
    if (!file) return;
    setBusy(true); setErr(""); setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/lms/labs/${lab.id}/cover`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMsg(`Cover image updated for ${lab.code}.`);
      await load();
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const deleteLab = async (lab) => {
    if (!window.confirm(`PERMANENTLY delete ${lab.code} — ${lab.name}?\n\nThis also removes its modules, lessons, and assessments. Use "Hide" for soft-delete instead.`)) return;
    setBusy(true); setErr(""); setMsg("");
    try {
      await api.delete(`/admin/lms/labs/${lab.id}`);
      setMsg(`Deleted ${lab.code}.`);
      await load();
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const deleteCover = async (lab) => {
    if (!window.confirm(`Remove cover image for ${lab.code} — ${lab.name}?`)) return;
    setBusy(true); setErr(""); setMsg("");
    try {
      await api.delete(`/admin/lms/labs/${lab.id}/cover`);
      setMsg(`Cover image removed for ${lab.code}.`);
      await load();
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-coe-labs-page">
      <div className="rounded-sm p-6 text-white bg-brand-gradient flex items-start justify-between gap-3 flex-wrap">
        <div>
          <span className="eyebrow on-grad-muted">Admin · CoE Labs</span>
          <h1 className="font-display font-extrabold text-2xl md:text-3xl mt-1">Manage CoE labs</h1>
          <p className="text-sm text-white/80 mt-1 max-w-xl">
            Edit lab cover images, names, descriptions, badges, and SC governance metadata. Hide labs from the public site without losing data.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            disabled={busy || loading}
            className="btn-on-gradient inline-flex items-center gap-1.5"
            data-testid="admin-labs-refresh"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            type="button"
            onClick={() => setEditing({ ...EMPTY_LAB })}
            className="btn-on-gradient inline-flex items-center gap-1.5"
            data-testid="admin-labs-add"
          >
            <Plus size={14} /> Add lab
          </button>
        </div>
      </div>

      {err && <div className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2" data-testid="admin-labs-error">{err}</div>}
      {msg && <div className="text-sm text-emerald-700 bg-emerald-50 rounded-xl px-3 py-2" data-testid="admin-labs-msg">{msg}</div>}

      <div className="card card-pad">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted py-6"><Loader2 size={14} className="animate-spin" /> Loading…</div>
        ) : labs.length === 0 ? (
          <div className="border-2 border-dashed rounded-xl p-8 text-center" style={{ borderColor: "var(--rule)" }}>
            <div className="font-semibold text-sm">No labs yet</div>
            <button type="button" onClick={() => setEditing({ ...EMPTY_LAB })} className="btn-primary mt-4 inline-flex items-center gap-2">
              <Plus size={14} /> Add your first lab
            </button>
          </div>
        ) : (
          <div className="space-y-2.5">
            {labs.map((lab) => (
              <LabRow
                key={lab.id}
                lab={lab}
                busy={busy}
                onEdit={setEditing}
                onToggleHidden={toggleHidden}
                onDelete={deleteLab}
                onUploadCover={uploadCover}
                onDeleteCover={deleteCover}
              />
            ))}
          </div>
        )}
        <p className="text-[11px] text-muted mt-4">
          💡 <strong>Hide</strong> (eye icon) keeps data intact and just removes the lab from the public listing.
          <strong> Delete</strong> (trash icon) permanently removes the lab and ALL its modules, lessons, and assessments.
        </p>
      </div>

      {editing && (
        <LabEditModal
          lab={editing}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
